/**
 * FairCoreGridView — v2.0 demo-path beat 3 (FAIR Core grade grid).
 *
 * Replaces the Cytoscape assessment graph as the primary surface at
 * /analysis/:job_id/fair-core, following the One Compass prototype
 * (docs/design/prototypes/03-fair-core.html): sixteen FAIR principles graded
 * pass · partial · fail, grouped by pillar, each row an inline expander whose
 * second level is the evidence behind the grade (signals, thresholds, SHACL
 * shape, the F2 maturity ladder, and any HITL decision overlay).
 *
 * Data: GET /api/analysis/:job_id/evaluate (useEvaluateData) — the same
 * HITL-adjudicated payload the Evaluate surface uses, so grades here always
 * agree with the decisions ledger. The old graph stays reachable at
 * /analysis/:job_id/fair-core/graph.
 */
import React, { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEvaluateData, type FairRuleResult } from '../hooks/useEvaluateData';
import './fair-core-grid.css';

// ---------------------------------------------------------------------------
// Grading
// ---------------------------------------------------------------------------

type Grade = 'pass' | 'partial' | 'fail' | 'na';

function gradeOf(rule: FairRuleResult): Grade {
  if (rule.status === 'NOT_APPLICABLE') return 'na';
  const mb = rule.evidence_structured?.maturity_breakdown;
  if (mb && mb.tier_max > 0) {
    if (mb.tier_achieved <= 0) return 'fail';
    if (mb.tier_achieved >= mb.tier_max) return 'pass';
    return 'partial';
  }
  return rule.status === 'PASS' ? 'pass' : 'fail';
}

const GRADE_LABEL: Record<Grade, string> = {
  pass: 'pass',
  partial: 'partial',
  fail: 'fail',
  na: 'n/a',
};

const PILLAR_ORDER = ['F', 'A', 'I', 'R'] as const;
const PILLAR_NAMES: Record<string, string> = {
  F: 'Findable',
  A: 'Accessible',
  I: 'Interoperable',
  R: 'Reusable',
};

/**
 * Canonical principle titles (GO FAIR wording, compressed). Older analysis
 * jobs persisted ruleName == rule id; fall back to these so every row reads
 * as a principle, not a code.
 */
const RULE_TITLES: Record<string, string> = {
  F1: 'Data has a globally unique, persistent identifier',
  F2: 'Data is described with rich metadata',
  F3: 'Metadata explicitly includes the data identifier',
  F4: 'Data is registered/indexed in a searchable resource',
  A1: 'Retrievable by identifier over a standard protocol',
  'A1.1': 'Protocol is open, free, universally implementable',
  'A1.2': 'Protocol supports authentication & authorization',
  A2: 'Metadata persists even when the data is gone',
  I1: 'Uses a formal, accessible knowledge representation',
  I2: 'Uses vocabularies that follow FAIR principles',
  I3: 'Includes qualified references to other (meta)data',
  I4: 'Aligned with recognized domain standards',
  R1: 'Richly described with relevant attributes',
  'R1.1': 'Released with a clear, accessible usage license',
  'R1.2': 'Associated with detailed provenance',
  'R1.3': 'Meets domain-relevant community standards',
};

function ruleTitle(rule: FairRuleResult): string {
  if (rule.ruleName && rule.ruleName !== rule.rule) return rule.ruleName;
  return RULE_TITLES[rule.rule] ?? rule.ruleName;
}

function pillarStrength(score: number): string {
  if (score >= 4) return 'strong';
  if (score >= 3) return 'good';
  if (score >= 2) return 'partial';
  return 'weak';
}

function evidenceBadge(evaluatedBy: FairRuleResult['evaluatedBy']): { label: string; cls: string } {
  switch (evaluatedBy) {
    case 'shacl':
      return { label: 'SHACL', cls: 'fcg-ev-badge--shacl' };
    case 'human':
    case 'human_with_machine_concur':
      return { label: 'Human decision', cls: 'fcg-ev-badge--human' };
    case 'none':
      return { label: 'Not evaluated', cls: 'fcg-ev-badge--meta' };
    case 'supplementary':
    default:
      return { label: 'Metadata', cls: 'fcg-ev-badge--meta' };
  }
}

// Natural sort for rule codes: F1 < F2 < A1 < A1.1 < A2 ...
function ruleSort(a: FairRuleResult, b: FairRuleResult): number {
  return a.rule.localeCompare(b.rule, undefined, { numeric: true });
}

// ---------------------------------------------------------------------------
// View
// ---------------------------------------------------------------------------

export const FairCoreGridView: React.FC = () => {
  const { job_id } = useParams<{ job_id: string }>();
  const navigate = useNavigate();
  const { data, loading, error } = useEvaluateData(job_id);

  const model = useMemo(() => {
    if (!data) return null;
    const byPillar = PILLAR_ORDER.map((p) => ({
      pillar: p,
      name: PILLAR_NAMES[p],
      score: data.pillar_scores[p] ?? 0,
      rules: data.rules.filter((r) => r.pillar === p).sort(ruleSort),
    }));
    const grades = data.rules.map(gradeOf);
    const counts = {
      pass: grades.filter((g) => g === 'pass').length,
      partial: grades.filter((g) => g === 'partial').length,
      fail: grades.filter((g) => g === 'fail').length,
      na: grades.filter((g) => g === 'na').length,
    };
    const evaluated = data.rules.length - counts.na;

    // Deterministic synthesis copy from pillar ranking — no AI call.
    const ranked = [...byPillar].sort((a, b) => b.score - a.score);
    const strongest = ranked[0];
    const weakest = ranked[ranked.length - 1];
    const failsByPillar = PILLAR_ORDER.map((p) => ({
      pillar: p,
      fails: data.rules.filter((r) => r.pillar === p && gradeOf(r) === 'fail').length,
    })).sort((a, b) => b.fails - a.fails);
    const failCluster =
      counts.fail >= 2 && failsByPillar[0].fails >= Math.ceil(counts.fail / 2)
        ? PILLAR_NAMES[failsByPillar[0].pillar]
        : null;

    return { byPillar, counts, evaluated, strongest, weakest, failCluster };
  }, [data]);

  // ---- shell states -------------------------------------------------------

  if (loading) {
    return (
      <div className="fcg-page">
        <div className="fcg-inner">
          <h1 className="fcg-headline">FAIR Core</h1>
          <p className="fcg-muted" role="status">Loading FAIR assessment…</p>
        </div>
      </div>
    );
  }

  if (error || !data || !model) {
    const isPreHitl =
      !!error && (error.includes('INVALID_STATE') || error.includes('no FAIR rule results') || error.includes('422'));
    return (
      <div className="fcg-page">
        <div className="fcg-inner">
          <h1 className="fcg-headline">FAIR Core</h1>
          <div className="fcg-error" role="alert">
            {isPreHitl
              ? 'This analysis predates structured FAIR rule results. Re-run the analysis to grade the sixteen principles.'
              : `Failed to load FAIR assessment${error ? `: ${error}` : '.'}`}
          </div>
        </div>
      </div>
    );
  }

  const { byPillar, counts, evaluated, strongest, weakest, failCluster } = model;
  const ringCircumference = 2 * Math.PI * 58;
  const ringFill = evaluated > 0 ? (counts.pass / evaluated) * ringCircumference : 0;

  return (
    <div className="fcg-page">
      <div className="fcg-inner">
        <button type="button" className="fcg-back" onClick={() => navigate(-1)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back
        </button>

        <div className="fcg-eyebrow">FAIR Assessment &middot; Technical leg</div>
        <h1 className="fcg-headline">FAIR Core</h1>
        <p className="fcg-subhead">
          Sixteen FAIR principles, graded pass &middot; partial &middot; fail &mdash; click any principle for the
          evidence behind its grade.
        </p>

        {/* synthesis band */}
        <div className="fcg-band">
          <div className="fcg-hero">
            <div className="fcg-ring" role="img" aria-label={`${counts.pass} of ${evaluated} principles pass`}>
              <svg width="132" height="132" viewBox="0 0 132 132">
                <circle cx="66" cy="66" r="58" fill="none" className="fcg-ring-track" strokeWidth="11" />
                <circle
                  cx="66"
                  cy="66"
                  r="58"
                  fill="none"
                  className="fcg-ring-fill"
                  strokeWidth="11"
                  strokeLinecap="round"
                  strokeDasharray={`${ringFill} ${ringCircumference}`}
                />
              </svg>
              <div className="fcg-ring-val">
                <span className="fcg-ring-num">{counts.pass}</span>
                <span className="fcg-ring-den">of {evaluated} pass</span>
              </div>
            </div>
            <div>
              <div className="fcg-band-eyebrow">FAIR maturity &mdash; technical</div>
              <p className="fcg-band-headline">
                <b>
                  {strongest.name} today
                  {strongest.pillar !== weakest.pillar ? `, not yet ${weakest.name}.` : '.'}
                </b>{' '}
                {strongest.pillar !== weakest.pillar
                  ? `${strongest.name} scores ${strongest.score.toFixed(1)} of 5 while ${weakest.name} sits at ${weakest.score.toFixed(1)} — the gap is where reuse breaks down.`
                  : `All four pillars score within a narrow band around ${strongest.score.toFixed(1)} of 5.`}
              </p>
              <p className="fcg-band-sub">
                {counts.pass} pass &middot; {counts.partial} partial &middot; {counts.fail} fail
                {counts.na > 0 ? <> &middot; {counts.na} n/a</> : null}
                {failCluster ? <>. The fails cluster in {failCluster}.</> : null}
              </p>
            </div>
          </div>
        </div>

        {/* pillar summary */}
        <div className="fcg-pillars">
          {byPillar.map((p) => (
            <div className="fcg-pillar" key={p.pillar}>
              <div className="fcg-pillar-letter">{p.pillar}</div>
              <div className="fcg-pillar-name">{p.name}</div>
              <div className="fcg-pillar-meter">
                <i
                  className={`fcg-meter--${pillarStrength(p.score) === 'weak' ? 'fail' : pillarStrength(p.score) === 'partial' ? 'warn' : 'pass'}`}
                  style={{ width: `${Math.round((p.score / 5) * 100)}%` }}
                />
              </div>
              <div className="fcg-pillar-frac">
                <b>{p.score.toFixed(1)}</b> / 5 &middot; {pillarStrength(p.score)}
              </div>
            </div>
          ))}
        </div>

        {/* principle groups */}
        {byPillar.map((group) => (
          <div className="fcg-group" key={group.pillar}>
            <p className="fcg-section-label">{group.name}</p>
            {group.rules.map((rule) => (
              <PrincipleRow key={rule.rule} rule={rule} />
            ))}
            {group.rules.length === 0 && <p className="fcg-muted">No rules evaluated for this pillar.</p>}
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Principle row (collapsed = grade + diagnosis · expanded = evidence)
// ---------------------------------------------------------------------------

const PrincipleRow: React.FC<{ rule: FairRuleResult }> = ({ rule }) => {
  const grade = gradeOf(rule);
  const ev = rule.evidence_structured;
  const mb = ev?.maturity_breakdown;
  const badge = evidenceBadge(rule.evaluatedBy);
  const decision = ev?.human_decision;

  return (
    <details className={`fcg-principle is-${grade}`} data-testid={`principle-${rule.rule}`}>
      <summary>
        <div className="fcg-glyph" aria-hidden="true" />
        <div className="fcg-code">
          {rule.rule}
          <small>{GRADE_LABEL[grade]}</small>
        </div>
        <div className="fcg-body">
          <p className="fcg-title">{ruleTitle(rule)}</p>
          <p className="fcg-diag">{rule.description}</p>
          {mb && mb.elements_missing.length > 0 && grade !== 'pass' && (
            <p className="fcg-fix">
              <b>Close it:</b> populate {mb.elements_missing.join(', ')}.
            </p>
          )}
        </div>
        <div className="fcg-right">
          <span className={`fcg-chip fcg-chip--${grade}`}>{GRADE_LABEL[grade]}</span>
          {mb && (
            <div className="fcg-tier">
              Tier {mb.tier_achieved} / {mb.tier_max}
            </div>
          )}
          <div className="fcg-chev">
            <span className="when-closed">evidence</span>
            <span className="when-open">close</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
              <path d="m9 6 6 6-6 6" />
            </svg>
          </div>
        </div>
      </summary>

      <div className="fcg-evidence">
        <div className="fcg-ev-head">
          <span className="fcg-ev-lbl">Evidence</span>
          <span className={`fcg-ev-badge ${badge.cls}`}>{badge.label}</span>
        </div>

        {rule.evidence && <p className="fcg-ev-note">{rule.evidence}</p>}

        {(ev?.threshold || ev?.measured) && (
          <table className="fcg-ev-table">
            <thead>
              <tr>
                {ev.threshold && <th>Threshold</th>}
                {ev.measured && <th>Measured</th>}
              </tr>
            </thead>
            <tbody>
              <tr>
                {ev.threshold && <td><b>{ev.threshold}</b></td>}
                {ev.measured && <td>{ev.measured}</td>}
              </tr>
            </tbody>
          </table>
        )}

        {ev?.signals && ev.signals.length > 0 && (
          <table className="fcg-ev-table">
            <thead>
              <tr>
                <th>Signal</th>
                <th>Location</th>
                <th>Value</th>
                <th>Role</th>
              </tr>
            </thead>
            <tbody>
              {ev.signals.map((s, i) => (
                <tr key={i}>
                  <td><b>{s.type}</b></td>
                  <td>{s.location}</td>
                  <td>{s.value}</td>
                  <td>{s.role}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {ev?.shacl_shape_link && (
          <p className="fcg-ev-note">
            Shape <code>{ev.shacl_shape_link.shape_name}</code>
            {ev.shacl_shape_link.source_path ? <> &middot; {ev.shacl_shape_link.source_path}</> : null}
          </p>
        )}

        {mb && mb.tier_definitions.length > 0 && (
          <div className="fcg-ev-ladder">
            {mb.tier_definitions.map((t) => {
              const cls = t.tier < mb.tier_achieved ? 'is-met' : t.tier === mb.tier_achieved ? 'is-now' : '';
              return (
                <div className={`fcg-ev-rung ${cls}`} key={t.tier}>
                  <b>
                    Tier {t.tier}
                    {t.tier < mb.tier_achieved ? ' · met' : t.tier === mb.tier_achieved ? ' · current' : ''}
                  </b>
                  {t.label}
                </div>
              );
            })}
          </div>
        )}

        {mb && (mb.elements_present.length > 0 || mb.elements_missing.length > 0) && (
          <div className="fcg-ev-elements">
            {mb.elements_present.map((el) => (
              <span className="fcg-ev-el is-pass" key={`p-${el}`}>
                {el} &#10003;
              </span>
            ))}
            {mb.elements_missing.map((el) => (
              <span className="fcg-ev-el is-fail" key={`m-${el}`}>
                {el} &#10007;
              </span>
            ))}
          </div>
        )}

        {decision && (
          <p className="fcg-ev-decision">
            Human decision: <b>{decision.action}</b> by {decision.decided_by.display_name} on{' '}
            {new Date(decision.decided_at).toLocaleDateString()}
            {decision.rationale ? <> &mdash; &ldquo;{decision.rationale}&rdquo;</> : null}
            {decision.stale ? <span className="fcg-ev-stale"> stale</span> : null}
          </p>
        )}
      </div>
    </details>
  );
};

export default FairCoreGridView;
